import NextLink from 'next/link';
import { useRouter } from 'next/router';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

import { useNavTracking } from 'partials/nav/analytics';
import type { NavItemIdentity } from 'partials/nav/navEvents';
import { appendQueryString } from 'utils/utils';
import { useSanityInteractionTracking } from './analytics';

type TrackedLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  as?: string;
  elementLabel?: string;
  href: string;
  includeQueryString?: boolean;
  nav?: boolean;
  navItem?: NavItemIdentity;
  reloadDocument?: boolean;
};

const TrackedLink = ({
  as,
  elementLabel,
  href,
  includeQueryString = false,
  nav = false,
  navItem,
  onClick,
  reloadDocument = false,
  ...props
}: TrackedLinkProps) => {
  const router = useRouter();
  const trackClick = useSanityInteractionTracking();
  const { trackClick: trackNavClick } = useNavTracking(navItem);

  const hrefWithQueryString = includeQueryString ? appendQueryString(href, router.asPath) : href;

  const trackLinkClick = (ev: MouseEvent<HTMLAnchorElement>) => {
    const hrefBeforeClick = ev.currentTarget.getAttribute('href');

    onClick?.(ev);

    const hrefAfterClick = ev.currentTarget.getAttribute('href');

    if (!ev.defaultPrevented) {
      const clickedHref = hrefAfterClick === hrefBeforeClick ? (as ?? href) : (hrefAfterClick ?? undefined);

      if (nav) {
        trackNavClick(ev, clickedHref ?? href);
      } else {
        trackClick(ev, { elementLabel, href: clickedHref });
      }
    }
  };

  if (reloadDocument) {
    return <a {...props} href={hrefWithQueryString} onClick={trackLinkClick} />;
  }

  return <NextLink {...props} href={hrefWithQueryString} as={as} onClick={trackLinkClick} />;
};

export default TrackedLink;
